import { useQuery } from '@tanstack/react-query';
import type { DeploymentMetricsResponse } from 'api/deployment/handlers/deploymentMetricsHandler';
import { DeploymentMetricType } from 'common/CommonConstants';
import { DeploymentMetricsRequest } from 'common/deployment/DeploymentApiRoutesDocuments';
import type { DeploymentMetricsNumericRow } from 'common/deployment/MetricsDocument';
import Api from 'client/lib/Api';
import { Statistic } from 'client/components/Statistic';

const loadData = async (deploymentID: number) => {
  const response = await Api.post<DeploymentMetricsRequest, DeploymentMetricsResponse>('/deployment/metrics', {
    deploymentID,
    type: DeploymentMetricType.MEMORY,
    resolutionMinutes: 1,
  });

  if (response.status !== 'ok') {
    throw new Error('Failed to load CPU metrics');
  }

  const currentMemory = (response.data as DeploymentMetricsNumericRow[]).at(-1)?.value || 0;
  const totalMemory = (response as any).totalMemory as number;

  return { currentMemory, totalMemory };
};

export interface RamGraphProps {
  deploymentID?: number;
}

export const RamGraph = ({ deploymentID }: RamGraphProps) => {
  const query = useQuery({
    // Cache query based on deploymentID & period
    queryKey: ['metrics.ram', deploymentID],
    queryFn: () => loadData(deploymentID!),
    enabled: !!deploymentID,
    retryDelay: 10000,

    // Refresh after 10 seconds
    refetchInterval: 10000,
  });

  const currentMemory = query.data?.currentMemory || 0;
  const totalMemory = query.data?.totalMemory || 0;
  const currentMemoryInGb = currentMemory / Math.pow(1000, 3);
  const totalMemoryInGb = (totalMemory / Math.pow(1000, 3)).toFixed(0);

  return (
    <Statistic
      title="RAM"
      amount={currentMemoryInGb}
      decimals={2}
      unit="GB"
      max={totalMemoryInGb}
      progress={(currentMemory / totalMemory) * 100}
    />
  );
};
